import { createFileRoute, useNavigate } from "@tanstack/solid-router";
import {
  createEffect,
  createMemo,
  createSignal,
  ErrorBoundary,
  on,
  Show,
  Suspense,
} from "solid-js";
import { Breadcrumb } from "~/components/ui/Breadcrumb";
import { Button } from "~/components/ui/button";
import { ErrorCard } from "~/components/ui/ErrorCard";
import { Skeleton } from "~/components/ui/skeleton";
import { ClaimsPanel } from "~/components/workbench/ClaimsPanel";
import { CorrelateDialog } from "~/components/workbench/CorrelateDialog";
import { CreateExcerptDialog } from "~/components/workbench/CreateExcerptDialog";
import { DetailPane } from "~/components/workbench/DetailPane";
import { ExcerptOverlayPanel } from "~/components/workbench/ExcerptOverlayPanel";
import { FileSwitcher } from "~/components/workbench/FileSwitcher";
import { ImageViewer } from "~/components/workbench/ImageViewer";
import { WorkbenchLayout } from "~/components/workbench/WorkbenchLayout";
import { useUpdateAttributeMutation } from "~/lib/queries/claims";
import {
  useListExcerptsQuery,
  useUpdateExcerptMutation,
} from "~/lib/queries/excerpts";
import { useListFilesQuery } from "~/lib/queries/files";
import { useProjectQuery } from "~/lib/queries/projects";
import { useSourceQuery } from "~/lib/queries/sources";
import { useWorkbench, WorkbenchProvider } from "~/lib/workbench/claims-store";
import {
  type Box,
  blobToBytes,
  cropImageToBlob,
} from "~/lib/workbench/crop-tool";
import { VocabProvider } from "~/lib/workbench/vocab-provider";

interface WorkbenchSearch {
  file?: string;
}

export const Route = createFileRoute(
  "/_authenticated/projects/$projectId/sources/$sourceId/workbench",
)({
  ssr: false,
  validateSearch: (search: Record<string, unknown>): WorkbenchSearch => ({
    file: typeof search.file === "string" ? search.file : undefined,
  }),
  component: WorkbenchPage,
});

function WorkbenchContent() {
  const params = Route.useParams();
  const search = Route.useSearch();
  const navigate = useNavigate();

  const projectQuery = useProjectQuery(() => params().projectId);
  const sourceQuery = useSourceQuery(
    () => params().projectId,
    () => params().sourceId,
  );

  const [representationId, setRepresentationId] = createSignal<
    string | undefined
  >(undefined);
  const [correlateOpen, setCorrelateOpen] = createSignal(false);

  const selectedFileId = createMemo(() => search().file);

  const setSelectedFileId = (fileId: string) => {
    navigate({
      to: "/projects/$projectId/sources/$sourceId/workbench",
      params: { projectId: params().projectId, sourceId: params().sourceId },
      search: (prev) => ({ ...prev, file: fileId }),
      replace: true,
    });
  };

  const projectName = () => projectQuery.data?.name ?? "Project";
  const sourceCitation = () => sourceQuery.data?.citation ?? "Source";

  return (
    <div class="flex h-[calc(100vh-(--spacing(16)))] min-h-0 flex-col">
      <div class="flex items-center justify-between border-b border-border px-4 py-3">
        <div class="space-y-1">
          <Breadcrumb
            items={[
              { label: projectName(), to: `/projects/${params().projectId}` },
              {
                label: "Sources",
                to: `/projects/${params().projectId}/sources`,
              },
              {
                label: sourceCitation(),
                to: `/projects/${params().projectId}/sources/${params().sourceId}`,
              },
              { label: "Workbench" },
            ]}
          />
          <h1 class="text-sm font-bold text-foreground">Claims Workbench</h1>
        </div>
        <Button onClick={() => setCorrelateOpen(true)}>Correlate</Button>
      </div>

      <CorrelateDialog
        projectId={params().projectId}
        sourceId={params().sourceId}
        open={correlateOpen()}
        onOpenChange={setCorrelateOpen}
      />

      <FileSwitcher
        projectId={params().projectId}
        sourceId={params().sourceId}
        fileId={selectedFileId()}
        representationId={representationId()}
        onSelectRepresentation={setRepresentationId}
        onSelectFile={setSelectedFileId}
      />

      <div class="flex-1 min-h-0">
        <WorkbenchLayout
          viewer={
            <ViewerPane
              projectId={params().projectId}
              sourceId={params().sourceId}
              representationId={representationId()}
              fileId={selectedFileId()}
            />
          }
          panel={
            <div class="flex h-full min-h-0 flex-col">
              <div class="min-h-0 flex-1 overflow-y-auto">
                <ClaimsPanel
                  projectId={params().projectId}
                  sourceId={params().sourceId}
                  fileId={selectedFileId()}
                />
              </div>
              <DetailPane
                projectId={params().projectId}
                sourceId={params().sourceId}
                fileId={selectedFileId()}
              />
            </div>
          }
        />
      </div>
    </div>
  );
}

function ViewerPane(props: {
  projectId: string;
  sourceId: string;
  representationId: string | undefined;
  fileId: string | undefined;
}) {
  // The FileSwitcher already fetches this same list (same query key), so
  // this is a cache hit rather than a duplicate request in practice.
  const filesQuery = useListFilesQuery(
    () => props.projectId,
    () => props.representationId ?? "",
  );

  const file = createMemo(() =>
    filesQuery.data?.find((f) => f.id === props.fileId),
  );

  const excerptsQuery = useListExcerptsQuery(
    () => props.projectId,
    () => props.fileId ?? "",
  );
  const workbench = useWorkbench();
  createEffect(
    on(
      () => props.fileId,
      () => workbench.clearExcerptSelection(),
      { defer: true },
    ),
  );
  const updateAttribute = useUpdateAttributeMutation(
    () => props.projectId,
    () => props.sourceId,
  );
  const updateExcerpt = useUpdateExcerptMutation(
    () => props.projectId,
    () => props.fileId ?? "",
  );

  const [pendingBox, setPendingBox] = createSignal<Box | null>(null);
  const [imageElement, setImageElement] = createSignal<HTMLImageElement | null>(
    null,
  );

  const handleSelectExcerpt = (id: string) => {
    const pendingAttributeId = workbench.pendingLink();
    if (pendingAttributeId) {
      updateAttribute.mutate(
        { attributeId: pendingAttributeId, supportedById: id },
        { onSuccess: () => workbench.cancelLink() },
      );
      return;
    }
    const fileId = props.fileId;
    if (!fileId) return;
    workbench.selectExcerpt({ id, originatesFromId: fileId });
  };

  const handleResizeExcerptBox = async (id: string, box: Box) => {
    const fileId = props.fileId;
    const image = imageElement();
    if (!fileId) return;
    let data: Uint8Array | undefined;
    if (image) {
      const blob = await cropImageToBlob(image, box);
      data = await blobToBytes(blob);
    }
    updateExcerpt.mutate({
      excerptId: id,
      box,
      data,
    });
  };

  return (
    <Show
      when={props.fileId}
      fallback={
        <div class="flex h-full items-center justify-center text-sm text-muted-foreground">
          Select a file to view.
        </div>
      }
    >
      <Show
        when={file()}
        fallback={
          <div class="flex h-full items-center justify-center">
            <Skeleton class="h-3/4 w-3/4 rounded-lg" />
          </div>
        }
      >
        {(f) => (
          <>
            <ImageViewer
              projectId={props.projectId}
              file={f()}
              onExcerptBoxReady={setPendingBox}
              onImageElement={setImageElement}
              excerpts={excerptsQuery.data
                ?.filter((e) => e.box !== undefined)
                .map((e) => ({
                  id: e.id,
                  box: {
                    x: e.box?.x ?? 0,
                    y: e.box?.y ?? 0,
                    w: e.box?.w ?? 0,
                    h: e.box?.h ?? 0,
                  },
                }))}
              selectedExcerptId={workbench.excerptSelection()?.id}
              onSelectExcerpt={handleSelectExcerpt}
              onResizeExcerptBox={handleResizeExcerptBox}
              linkArmed={!!workbench.pendingLink()}
              onCancelLinkArm={() => workbench.cancelLink()}
              onDeselectExcerpt={() => workbench.clearExcerptSelection()}
              panel={
                <Show when={workbench.excerptSelection()}>
                  {(sel) => (
                    <ExcerptOverlayPanel
                      projectId={props.projectId}
                      fileId={sel().originatesFromId}
                      excerptId={sel().id}
                      onClose={() => workbench.clearExcerptSelection()}
                    />
                  )}
                </Show>
              }
            />
            <Show when={imageElement()}>
              {(img) => (
                <CreateExcerptDialog
                  projectId={props.projectId}
                  originatesFromId={f().id}
                  pageNumber={1}
                  image={img()}
                  box={pendingBox()}
                  onOpenChange={(open) => {
                    if (!open) setPendingBox(null);
                  }}
                />
              )}
            </Show>
          </>
        )}
      </Show>
    </Show>
  );
}

function WorkbenchPage() {
  const params = Route.useParams();

  return (
    <ErrorBoundary
      fallback={(err) => (
        <ErrorCard
          title="Workbench error"
          message={err?.message ?? "An unexpected error occurred."}
        />
      )}
    >
      <Suspense fallback={<div>Loading workbench…</div>}>
        <VocabProvider projectId={() => params().projectId}>
          <WorkbenchProvider
            projectId={() => params().projectId}
            sourceId={() => params().sourceId}
          >
            <WorkbenchContent />
          </WorkbenchProvider>
        </VocabProvider>
      </Suspense>
    </ErrorBoundary>
  );
}
